import { TutorialConfig, TutorialStep, TutorialTask } from '../types';

const AUDACITY_TUTORIAL_PROJECT_ID = 'f85e1c59-0874-4d0b-83da-031885061fd3';

const AUDACITY_TUTORIAL_TASKS: TutorialTask[] = [
  {
    id: 'audacity-project-tour',
    name: 'Explore the labeling workspace',
    type: 'success',
  },
  {
    id: 'audacity-obvious-success',
    name: 'Obvious success example',
    type: 'success',
    runId: 'f24cb8ff-b5ab-4661-b0e7-f1506814e181',
  },
  {
    id: 'audacity-terminal-success',
    name: 'Terminal-based success',
    type: 'success',
    runId: '311be021-a430-4a4e-8c51-726bcce9f251',
  },
  {
    id: 'audacity-hybrid-success',
    name: 'Hybrid success example',
    type: 'success',
    runId: 'ec265fcf-8d46-4a3e-9a29-5ab4c38af878',
  },
  {
    id: 'audacity-code-failure',
    name: 'Code-only failure example',
    type: 'failure',
    runId: 'fa19e976-e1f0-4433-b62b-88538f6bf62a',
  },
  {
    id: 'audacity-precision',
    name: 'Precision matters example',
    type: 'failure',
    runId: 'cce683dc-e7e1-4377-a713-cceaa0952a1d',
  },
  {
    id: 'audacity-judgement',
    name: 'Judgement example',
    type: 'success',
    runId: 'd2f82d9a-1d5b-4bef-bd6c-50932e635f9d',
  },
];

const AUDACITY_TUTORIAL_STEPS: TutorialStep[] = [
  {
    id: 'audacity-project-welcome',
    title: 'Welcome to Audacity Labeling',
    content:
      'This tutorial gives you a quick tour of the labeling workspace. You can explore every part of the UI without affecting real data.\n\nOnce you claim a labeling project, you can click on the project to be brought to this page.\n\nIf you would like to exit the tutorial, just click "Exit Tutorial" on the top bar. You can always restart the tutorial by clicking "Show Tutorial."',
    targetSelector: '.problem-detail',
    action: 'next',
    position: 'bottom',
    scope: 'project',
    nextButtonLabel: 'Next Problem',
  },
  {
    id: 'audacity-problem-list',
    title: 'Assigned Problems',
    content:
      'Each card in this list represents a problem. The badge tracks how many runs you have labeled. Feel free to click through the list during the tutorial.',
    targetSelector: '.problem-list',
    action: 'explore',
    position: 'right',
    allowInteraction: true,
    scope: 'project',
  },
  {
    id: 'audacity-solution-upload',
    title: 'Solution Uploads',
    content:
      'Projects consist of two steps: first, you must upload the solution for the problem, and next, you should label all of the runs for that problem. When uploading your solution, **make sure that your uploaded filename matches exactly the filename of the solution requested in the prompt**.',
    targetSelector: '.solution-upload-section',
    action: 'next',
    position: 'left',
    scope: 'project',
    maxWidth: 320,
  },
  {
    id: 'audacity-bad-task-button',
    title: 'Mark Ambiguous Problems',
    content:
      'If the prompt is ambiguous or has multiple valid outcomes, mark it as a bad task here. Provide a concise reason so reviewers understand the issue. After marking as ambiguous, all runs will automatically be labeled and you will not have to upload a solution.',
    targetSelector: '.bad-task-btn',
    action: 'next',
    position: 'bottom',
    scope: 'project',
  },
  {
    id: 'solution-upload-requirement',
    title: 'Solution Required',
    content:
      'Important: You must upload your solution before you can select and label any runs. This ensures you have solved the problem yourself before evaluating the AI\'s attempts. Notice that the runs below are currently grayed out.',
    targetSelector: '.run-list',
    action: 'next',
    position: 'left',
    scope: 'project',
    maxWidth: 320,
  },
  {
    id: 'solution-upload-select-run',
    title: 'Runs to Review',
    content:
      'Every run shows a model attempt. Now you can select one to open the viewer so you can study exactly how the agent approached the task. Click on any run below to proceed.',
    targetSelector: '.run-list',
    action: 'click',
    position: 'left',
    allowInteraction: true,
    scope: 'project',
    nextButtonLabel: 'Select a run to proceed',
  },
  {
    id: 'audacity-viewer-overview',
    title: 'Review Each Attempt',
    content:
      'The video displays the AI solving the task. The AI has a computer environment consisting of many applications like Audacity, LibreOffice, Inkscape, Gimp, and Shotcut, and can use any of these applications to solve the task. The AI can also use programming and bash scripting. Anything is fair game as long as the AI solves the task as requested!',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
    disablePrevious: true,
  },
  {
    id: 'audacity-viewer-timeline',
    title: 'Timeline Controls',
    content:
      'Use the timeline to jump between frames. Speed controls and keyboard shortcuts help you inspect long attempts quickly without skipping critical moments.',
    targetSelector: '.timeline-container',
    action: 'next',
    position: 'top',
    scope: 'viewer',
  },
  {
    id: 'audacity-viewer-command-panel',
    title: 'Command History',
    content:
      'The agent can launch shell commands, scripts, or custom tools while it works. Every command it runs appears here so you can verify exactly what code or terminal actions were executed.',
    targetSelector: '.tool-output-section:first-child',
    action: 'next',
    position: 'top',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'audacity-viewer-thoughts',
    title: 'Agent Notes',
    content:
      'Read the thought bubble to see why the agent acted. When judging ambiguity, these notes often reveal whether the prompt had multiple valid interpretations.',
    targetSelector: '.thought-process',
    action: 'next',
    position: 'right',
    scope: 'viewer',
  },
  {
    id: 'audacity-viewer-inspect',
    title: 'Inspect the Attempt',
    content:
      'Watch the video carefully to see every step the AI took. When you need the final output, right-click the video to download the files it produced and compare them with your ground-truth solution.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'audacity-viewer-evaluate',
    title: 'Label the Run',
    content:
      'Use this panel to decide whether the run succeeded or failed. If you realize that a problem is ambiguous or has multiple correct solutions, return to the problem workspace, mark it as a bad task, and then move on to the next problem.',
    targetSelector: '.evaluation-buttons',
    action: 'next',
    position: 'top',
    maxWidth: 360,
    scope: 'viewer',
    offsetY: -50,
  },
  {
    id: 'audacity-viewer-return',
    title: 'Return to the Problem Workspace',
    content:
      'Click “Back to Problem” when you finish reviewing runs. That returns you to the overview so you can upload a solution or mark the problem ambiguous.',
    targetSelector: '.back-to-problem-btn',
    action: 'click',
    position: 'right',
    scope: 'viewer',
    allowInteraction: true,
  },
  {
    id: 'audacity-project-recap',
    title: 'Wrap Up the Problem',
    content:
      'From this workspace you can revisit the prompt, upload the required solution file, and finish labeling every run. Ambiguous problems can be waived entirely.',
    targetSelector: '.problem-detail',
    action: 'next',
    position: 'bottom',
    scope: 'project',
    disablePrevious: true,
  },
  {
    id: 'audacity-example-transition',
    title: 'Run Labeling Examples',
    content:
      'Now let’s review real AI runs and decide whether each is a success or a failure. We’ll walk through several examples together.',
    targetSelector: '.problem-detail',
    action: 'next',
    position: 'bottom',
    scope: 'project',
    nextButtonLabel: 'Next Problem',
  },
  {
    id: 'audacity-example-success-intro',
    title: 'Obvious Success',
    content:
      'In this run, the AI is required to pan the audio fully on the right channel. Watch as the AI drags the pan slider for the right channel all the way to 100%. This is obviously a success from the video—no download required to verify it.',
    targetSelector: '.task-description',
    action: 'next',
    position: 'bottom',
    offsetY: 40,
    scope: 'viewer',
    disablePrevious: true,
  },
  {
    id: 'audacity-example-success-evaluate',
    title: 'Why It Succeeds',
    content:
      'Because the requested change is visible in the UI and the export name matches exactly, you can confidently mark this run as a success. Sadly most runs aren’t this obvious.',
    targetSelector: '.evaluation-buttons',
    action: 'next',
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
  },
  {
    id: 'audacity-example-general-info',
    title: 'Most Runs Require Inspection',
    content:
      'Usually you cannot judge success from the video alone. The AI might edit files via scripts or make subtle mistakes. Always download the output and compare it with your solution.',
    targetSelector: '.screenshot-viewer',
    action: 'next',
    position: 'top',
    scope: 'viewer',
    maxWidth: 380,
    nextButtonLabel: 'Next Problem',
  },
  {
    id: 'audacity-terminal-warning',
    title: 'Code-Based Solutions Are Valid',
    content:
      "In this video, the AI doesn't even open Audacity at all. You might be tempted to label it a failure, but remember, the AI can write code to directly modify the file!",
    targetSelector: '.tool-output-wrapper',
    action: 'next',
    position: 'top',
    scope: 'viewer',
    disablePrevious: true,
  },
  {
    id: 'audacity-terminal-script',
    title: 'Bash Script Execution',
    content:
      'Here, we see the AI executed a bash script to directly modify the file.',
    targetSelector: '.tool-output-wrapper',
    action: 'seek-frame',
    seekFrame: 6,
    position: 'top',
    scope: 'viewer',
  },
  {
    id: 'audacity-terminal-intro',
    title: 'Remember to Download the Output',
    content:
      "It's not worth it to try understanding what exactly the AI's code did. Instead, you should right-click the video and download the output! It's much easier to directly inspect the output file.",
    targetSelector: '.tool-output-wrapper',
    action: 'next',
    position: 'top',
    scope: 'viewer',
  },
  {
    id: 'audacity-terminal-verification',
    title: 'Output Verification',
    content:
      "And indeed, if you open the output file, you'll see that the AI perfectly deleted the audio starting from the 41st second.",
    targetSelector: '.tool-output-wrapper',
    action: 'next',
    position: 'top',
    scope: 'viewer',
  },
  {
    id: 'audacity-terminal-conclusion',
    title: 'Terminal Success',
    content:
      'Even without UI interaction the AI solved the task. Because the exported file meets the requirements, label this run a success.',
    targetSelector: '.evaluation-buttons',
    action: 'evaluate',
    expectedEvaluation: 'success',
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
    nextButtonLabel: 'Next Problem',
    allowInteraction: true,
  },
  {
    id: 'audacity-combo-intro',
    title: 'Hybrid Workflow',
    content:
      'Sometimes, the AI will use Audacity AND code to solve the problem. In these cases, just watching the video can be downright misleading!',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    disablePrevious: true,
  },
  {
    id: 'audacity-combo-misleading',
    title: 'Appearances Can Deceive',
    content:
      "If we jump to the end of the video, it looks like the AI didn't do anything at all. The track is still a little over 20 seconds long, and you might be tempted to label the video a fail.",
    targetSelector: '.screenshot-wrapper',
    action: 'seek-frame',
    seekFrame: 47,
    position: 'left',
    scope: 'viewer',
  },
  {
    id: 'audacity-combo-code-check',
    title: 'Check the Code',
    content:
      "But don't jump to conclusions just yet! Notice how the AI wrote a bunch of code as well. Let's try downloading the output file and inspecting it directly.",
    targetSelector: '.tool-output-wrapper',
    action: 'seek-frame',
    seekFrame: 45,
    position: 'top',
    scope: 'viewer',
  },
  {
    id: 'audacity-combo-output-success',
    title: 'Perfect Output',
    content:
      'Again, if we open the output file, we see that the AI performed the task perfectly. Seconds 1 through 4 were deleted, and the final track is 3 seconds shorter than the input.',
    targetSelector: '.tool-output-wrapper',
    action: 'next',
    position: 'top',
    scope: 'viewer',
  },
  {
    id: 'audacity-combo-note',
    title: 'Extra Files Are Okay',
    content:
      'Astute observers will notice the AI created an extra file along the way. However, extra files are NOT considered additional modifications, so the final output still counts as a success.',
    targetSelector: '.tool-output-wrapper',
    action: 'next',
    position: 'top',
    scope: 'viewer',
  },
  {
    id: 'audacity-combo-conclusion',
    title: 'Mark It a Success',
    content:
      'Focus on the final deliverable. If the exported file matches the prompt exactly, you should label the run as a success. Be careful not to get tricked by the AI failing to use the Audacity application!',
    targetSelector: '.evaluation-buttons',
    action: 'evaluate',
    expectedEvaluation: 'success',
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
    nextButtonLabel: 'Next Problem',
    allowInteraction: true,
  },
  {
    id: 'audacity-code-fail-intro',
    title: "Code Isn't Always Correct",
    content:
      "It's worth explicitly stating that the AI's code is not always correct. Here's another example where the AI doesn't use Audacity at all and instead writes code to directly modify the file.",
    targetSelector: '.tool-output-wrapper',
    action: 'next',
    position: 'top',
    scope: 'viewer',
    disablePrevious: true,
  },
  {
    id: 'audacity-code-fail-inspection',
    title: 'Inspect the Output',
    content:
      "However, if you download and inspect the output file, you'll notice the AI's output is much shorter than the original track. This is because the AI increased the frequency AND speed by 20%.",
    targetSelector: '.tool-output-wrapper',
    action: 'next',
    position: 'top',
    scope: 'viewer',
  },
  {
    id: 'audacity-code-fail-conclusion',
    title: 'Label as Failure',
    content:
      'This additional modification makes the run a clear failure—no need to reverse engineer the code.',
    targetSelector: '.evaluation-buttons',
    action: 'evaluate',
    expectedEvaluation: 'fail',
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
    nextButtonLabel: 'Next Problem',
    allowInteraction: true,
  },
  {
    id: 'audacity-precision-intro',
    title: 'Precision Matters',
    content:
      'Sometimes the AI will make very subtle mistakes that are only slightly incorrect. However, because we require the AI to perfectly follow the task description, even subtle mistakes need to be marked as failures.',
    targetSelector: '.evaluation-buttons',
    action: 'next',
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
    disablePrevious: true,
  },
  {
    id: 'audacity-precision-example',
    title: 'Specific Example',
    content:
      'For example, this problem asks the AI to remove the 2nd half of the audio. Since the original track is 1 minute and 3.33 seconds long, the output track should be 31.66 seconds long. However, in this run, the output is 31.03 seconds. This difference is unacceptable. Even being 0.1 seconds off would be too much. However, a 0.001 second difference is probably acceptable.',
    targetSelector: '.tool-output-wrapper',
    action: 'seek-frame',
    seekFrame: 31,
    position: 'top',
    scope: 'viewer',
  },
  {
    id: 'audacity-precision-conclusion',
    title: 'Call It a Fail',
    content:
      "Label this run a failure. In the next problem, we'll try to more precisely define which differences are acceptable.",
    targetSelector: '.evaluation-buttons',
    action: 'evaluate',
    expectedEvaluation: 'fail',
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
    nextButtonLabel: 'Next Problem',
    allowInteraction: true,
  },
  {
    id: 'audacity-judgement-intro',
    title: 'Exercise Judgement',
    content:
      "Building on the previous point, we aren't actually demanding perfect equality between the AI's output and your provided solution. They should be very very similar (a 0.1 second length difference is definitely unacceptable for example) but differences that are almost entirely imperceptible should be allowed.",
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    disablePrevious: true,
  },
  {
    id: 'audacity-judgement-example',
    title: 'Fade Example',
    content:
      "For example, in this problem, the AI is asked to apply a fade to multiple different sections, and the creator solution uses Audacity's built-in fade feature.",
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
  },
  {
    id: 'audacity-judgement-custom-fade',
    title: 'Custom Fade Logic',
    content:
      "But in this run, the AI wrote its own fade logic from scratch. If you compare the AI's output to the Audacity solution and squint very very closely, you'll notice the fade shape is ever so slightly different. However, because the difference is so minor, I think this run should be labeled a success.",
    targetSelector: '.tool-output-wrapper',
    action: 'seek-frame',
    seekFrame: 9,
    position: 'top',
    scope: 'viewer',
  },
  {
    id: 'audacity-judgement-conclusion',
    title: 'Minor Deviations Are Okay',
    content:
      'I know this rule is very subjective, but we are counting on you to use your best judgement as audio engineers to determine when a difference is truly negligible. Feel free to send Max Kan a message if you have any questions!',
    targetSelector: '.evaluation-buttons',
    action: 'evaluate',
    expectedEvaluation: 'success',
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
    nextButtonLabel: 'Next Problem',
    allowInteraction: true,
  },
  {
    id: 'tutorial-complete',
    title: 'Nice Work!',
    content:
      'You have finished the Audacity tutorial. You now know how to tour the workspace, inspect outputs, and decide between success, fail, or bad task. Good luck on real projects!',
    action: 'next',
    position: 'bottom',
    scope: 'viewer',
  },
];

export const audacityTutorialConfig: TutorialConfig = {
  software: 'audacity',
  displayName: 'Audacity Labeling Tutorial',
  totalTasks: AUDACITY_TUTORIAL_TASKS.length,
  tasks: AUDACITY_TUTORIAL_TASKS,
  steps: AUDACITY_TUTORIAL_STEPS,
  completionKey: 'labelPlatform_audacity_TutorialCompleted',
  dataSource: 'api',
  projectId: AUDACITY_TUTORIAL_PROJECT_ID,
};
